import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Loader, Search, Star, TriangleAlert, Waypoints } from 'lucide-react';
import { api } from '../api';
import { fmtKm, fmtNum } from '../util/format';
import { useCarga, useDebounce } from '../util/hooks';

/** Troncales sincronizadas desde Mara, con las rutas que armó el despacho en el TMS. */
export function TroncalesPagina() {
  const navegar = useNavigate();
  const [params] = useSearchParams();
  const [texto, setTexto] = useState('');
  const [sinRutas, setSinRutas] = useState(params.get('sinRutas') === 'true');
  const [archivadas, setArchivadas] = useState(false);
  const q = useDebounce(texto.trim(), 300);

  const { datos, cargando, error, recargar } = useCarga(
    () => api.troncales({ sinRutas: sinRutas || undefined, archivadas: archivadas || undefined, q: q || undefined, tamanio: 200 }),
    [sinRutas, archivadas, q],
  );
  // Cuántas están pendientes, con el filtro de texto pero sin el de «sin rutas».
  const pendientes = useCarga(() => api.troncales({ sinRutas: true, tamanio: 1 }), []);

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <div className="eyebrow">DESPACHO / TRONCALES</div>
          <h1>Troncales</h1>
        </div>
        <span className="list-count">{datos ? `${datos.total} troncal${datos.total === 1 ? '' : 'es'}` : ''}</span>
      </div>

      {pendientes.datos && pendientes.datos.total > 0 && (
        <div className="banner aviso" role="status">
          <TriangleAlert aria-hidden />
          <span>
            <strong>
              {pendientes.datos.total} troncal{pendientes.datos.total === 1 ? '' : 'es'} sin rutas.
            </strong>{' '}
            Los viajes de esas troncales quedan sin ruta (sin paradas, desvío ni ETA) hasta que el despacho las rutee.
          </span>
          {!sinRutas && (
            <button type="button" className="btn-sm" style={{ marginLeft: 'auto' }} onClick={() => setSinRutas(true)}>
              Ver solo esas
            </button>
          )}
        </div>
      )}

      <div className="toolbar">
        <label className="field grow">
          <span>Buscar</span>
          <div className="search boxed">
            <Search aria-hidden />
            <input placeholder="Nombre, código del cliente, origen o destino" value={texto} onChange={(e) => setTexto(e.target.value)} />
          </div>
        </label>
        <label className="check-field">
          <input type="checkbox" checked={sinRutas} onChange={(e) => setSinRutas(e.target.checked)} />
          Solo sin rutas
        </label>
        <label className="check-field">
          <input type="checkbox" checked={archivadas} onChange={(e) => setArchivadas(e.target.checked)} />
          Incluir archivadas
        </label>
      </div>

      {error && (
        <div className="banner error" role="alert">
          <TriangleAlert aria-hidden />
          {error}
          <button type="button" className="btn-sm" style={{ marginLeft: 'auto' }} onClick={recargar}>
            Reintentar
          </button>
        </div>
      )}

      <div className="card">
        <div className="table-wrap">
          <table className="tabla">
            <thead>
              <tr>
                <th>Troncal</th>
                <th>Código</th>
                <th>Origen</th>
                <th>Destino</th>
                <th className="num">km referencia</th>
                <th>Rutas</th>
              </tr>
            </thead>
            <tbody>
              {datos?.items.map((t) => (
                <tr key={t.id} className="click" tabIndex={0} onClick={() => navegar(`/troncales/${t.id}`)} onKeyDown={(e) => e.key === 'Enter' && navegar(`/troncales/${t.id}`)}>
                  <td>
                    <Link to={`/troncales/${t.id}`} onClick={(e) => e.stopPropagation()}>
                      <strong>{t.nombre}</strong>
                    </Link>
                    {t.archivado && <small>Archivada en Mara</small>}
                  </td>
                  <td>{t.identificacion}</td>
                  <td>{t.origen ?? '—'}</td>
                  <td>{t.destino ?? '—'}</td>
                  <td className="num">{t.kmReferencia === null ? '—' : fmtKm(t.kmReferencia)}</td>
                  <td>
                    {t.cantidadRutas === 0 ? (
                      <span className="chip amber">
                        <TriangleAlert aria-hidden />
                        Sin rutas
                      </span>
                    ) : (
                      <span className="chip green">
                        <Waypoints aria-hidden />
                        {fmtNum(t.cantidadRutas)} ruta{t.cantidadRutas === 1 ? '' : 's'}
                        {t.rutaPorDefectoId !== null && <Star aria-label="Con ruta por defecto" />}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {cargando && !datos && (
          <div className="skeleton">
            <Loader className="spin" aria-hidden size={16} />
            Cargando troncales…
          </div>
        )}
        {datos && datos.items.length === 0 && (
          <div className="vacio">
            <Waypoints aria-hidden />
            No hay troncales con esos filtros.
          </div>
        )}
      </div>
    </div>
  );
}
